import React, { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineFolder, AiOutlineTag, AiOutlineDown, AiOutlineClose } from "react-icons/ai";
import DocTreeSidebar, { buildTreeFromFlat, findPath } from "../DocTreeSidebar/DocTreeSidebar";

// Construye el path "A / B / C" caminando hacia la raíz por `parent`.
// Acepta una lista combinada de folders + series (ambos con `parent`).
const buildPathLabel = (folderId, folders) => {
  if (!folderId) return "";
  const byId = new Map((folders || []).map((f) => [String(f.id), f]));
  const parts = [];
  let current = byId.get(String(folderId));
  let safety = 50;
  while (current && safety-- > 0) {
    parts.unshift(current.name);
    const parentId = current.parent != null ? String(current.parent) : null;
    current = parentId ? byId.get(parentId) : null;
  }
  return parts.join(" / ");
};

/**
 * Combo de selección de folder padre con vista en árbol — equivalente
 * funcional al `<select>` plano, pero respetando la jerarquía visual del
 * doctree. Internamente reusa `DocTreeSidebar` (con `noFetchCounts` y sin
 * pills) para que la UX sea idéntica a la sidebar de admin.
 *
 * - `folders`: lista plana de folders {id, name, parent}.
 * - `value`: id del folder seleccionado (string o null/""):
 * - `onChange(id)`: invocado al activar un folder en el árbol.
 * - `disabled`: si true, no abre el dropdown (modo lectura).
 * - `placeholder`: texto cuando no hay selección.
 */
const FolderTreePicker = ({
  folders,
  series,                          // opcional: si se pasa + allowSeries, las series aparecen en el árbol
  allowSeries = false,
  disableFolderSelection = false,  // si true, los folders son navegables pero NO seleccionables
  value,
  onChange,
  disabled = false,
  placeholder = "Seleccionar nodo padre…",
  storageKey = "boveda.folder-picker.expanded",
  id,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Tree: solo folders por default. Si allowSeries=true, las series se intercalan
  // como hijas (no expandibles) y son seleccionables.
  const tree = useMemo(
    () => buildTreeFromFlat(folders || [], allowSeries ? (series || []) : []),
    [folders, series, allowSeries]
  );

  // Para resolver el path del valor actual SIEMPRE consideramos folders+series,
  // incluso cuando el picker no permite seleccionar series. Esto es útil cuando
  // el caller pre-fillea `value` con el id de una serie (ej. flujo contextual
  // de ACLs): el trigger muestra "CNBA / DEPAL / FICHAS" aunque el árbol abierto
  // solo liste folders.
  const lookupNodes = useMemo(
    () => [...(folders || []), ...(series || [])],
    [folders, series]
  );

  // Path actual para mostrar en el trigger.
  const currentLabel = useMemo(() => {
    if (!value) return "";
    const lbl = buildPathLabel(value, lookupNodes);
    return lbl || String(value);
  }, [value, lookupNodes]);

  // ¿El valor seleccionado es una serie? Cambia el icono del trigger.
  const selectedIsSeries = useMemo(() => {
    if (!value) return false;
    return (series || []).some((s) => String(s.id) === String(value));
  }, [value, series]);

  // selectedPath para que el árbol auto-expanda ancestros y resalte la selección.
  const selectedPath = useMemo(() => {
    if (!value) return [];
    return findPath(tree, value) || [];
  }, [value, tree]);

  // Click outside para cerrar el panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleNodeActivate = (path) => {
    if (!path || path.length === 0) return;
    const last = path[path.length - 1];
    // Filtros de selección:
    //   - si NO se permiten series y el nodo es serie → ignorar.
    //   - si las folders están deshabilitadas y el nodo es folder → ignorar
    //     (igual el árbol expand/collapsea, eso lo maneja el TreeNode antes).
    if (last.isSeries && !allowSeries) return;
    if (!last.isSeries && disableFolderSelection) return;
    onChange && onChange(last.id);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange && onChange("");
  };

  return (
    <div ref={rootRef} style={styles.root} data-testid="folder-tree-picker">
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        style={{
          ...styles.trigger,
          ...(disabled ? styles.triggerDisabled : {}),
          ...(open ? styles.triggerOpen : {}),
        }}
        data-testid="folder-tree-picker-trigger"
        aria-haspopup="tree"
        aria-expanded={open}
      >
        {selectedIsSeries ? (
          <AiOutlineTag style={styles.triggerIcon} />
        ) : (
          <AiOutlineFolder style={styles.triggerIcon} />
        )}
        <span style={styles.triggerText} title={currentLabel || placeholder}>
          {currentLabel || <span style={styles.placeholder}>{placeholder}</span>}
        </span>
        {value && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            onClick={handleClear}
            style={styles.clearBtn}
            title="Limpiar selección"
            data-testid="folder-tree-picker-clear"
          >
            <AiOutlineClose />
          </span>
        )}
        <AiOutlineDown style={styles.chevron} />
      </button>
      {open && !disabled && (
        <div style={styles.panel} data-testid="folder-tree-picker-panel">
          {tree.length === 0 ? (
            <p style={styles.empty}>No hay folders disponibles</p>
          ) : (
            <DocTreeSidebar
              folders={tree}
              selectedPath={selectedPath}
              onFolderClick={handleNodeActivate}
              title=""
              storageKey={storageKey}
              noFetchCounts
              emptyMessage="No hay folders"
              containerStyle={{
                width: "100%",
                borderRight: "none",
                backgroundColor: "#FFFFFF",
                padding: "8px",
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  root: {
    position: "relative",
    width: "100%",
  },
  trigger: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "8px 10px",
    background: "#FFFFFF",
    border: "1px solid #D1D5DB",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#111827",
    textAlign: "left",
    gap: "8px",
    minHeight: "38px",
  },
  triggerOpen: {
    borderColor: "#00468B",
    boxShadow: "0 0 0 2px rgba(0,70,139,0.15)",
  },
  triggerDisabled: {
    backgroundColor: "#F3F4F6",
    color: "#9CA3AF",
    cursor: "not-allowed",
  },
  triggerIcon: {
    color: "#00468B",
    fontSize: "16px",
    flexShrink: 0,
  },
  triggerText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  placeholder: {
    color: "#9CA3AF",
  },
  clearBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    color: "#6B7280",
    cursor: "pointer",
    flexShrink: 0,
    fontSize: "12px",
  },
  chevron: {
    color: "#6B7280",
    fontSize: "12px",
    flexShrink: 0,
  },
  panel: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    minWidth: "280px",
    maxHeight: "320px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #D1D5DB",
    borderRadius: "6px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  empty: {
    padding: "12px",
    color: "#6B7280",
    fontSize: "13px",
    margin: 0,
  },
};

export default FolderTreePicker;
export { buildPathLabel };
